<style scoped>
.verify-code-input {
    @apply appearance-none;
    @apply border border-darkGrayishBlue;
    @apply w-full max-w-xs;
    @apply p-4;
    @apply text-center;
    @apply text-3xl text-black font-bold leading-normal;
    height: calc(
        2px + 2 * theme('spacing.4') + theme('fontSize.3xl') *
            theme('lineHeight.normal')
    );
}

.verify-code-input:focus {
    @apply outline-none;
    @apply border-blue;
}
</style>

<template>
    <div class="mt-4 px-2 md:pl-16 md:pr-4">
        <div v-if="!!loading" class="flex justify-center m-3 w-full">
            <spinner />
        </div>
        <div v-if="!loading">
            <div class="w-full text-lg font-bold">
                <div>
                    {{ eventTicket.prodBrndName }}
                </div>
                <div>
                    {{ eventTicket.prodName }}
                </div>
                <div>
                    {{ eventTicket.prodSizeText }}
                </div>
                <div
                    v-if="
                        eventTicket.evntTckMltpQtyInd === 'Y' &&
                            eventTicket.evntTckQty
                    "
                    class="my-2"
                >
                    {{ `${$t('event.ticket.qty')}: ${eventTicket.evntTckQty}` }}
                </div>
            </div>
            <div class="block sm:flex sm:flex-wrap sm:justify-between mt-4">
                <div class="w-full md:w-5/12 mb-2 flex">
                    <div class="w-32 flex-shrink-0">
                        {{ '門票編號' }}
                    </div>
                    <div>{{ eventTicket.evntTckNum }}</div>
                </div>
                <div class="w-full md:w-5/12 mb-2 flex">
                    <div class="w-32 flex-shrink-0">
                        {{ '購買日期' }}
                    </div>
                    <div>
                        {{
                            eventTicket.evntTckCreTs
                                | formatDate('fullDateTime')
                        }}
                    </div>
                </div>
                <div class="w-full md:w-5/12 mb-2 flex">
                    <div class="w-32 flex-shrink-0">
                        {{ '會員編號' }}
                    </div>
                    <div>{{ eventTicket.custId }}</div>
                </div>
                <div class="w-full md:w-5/12 mb-2 flex">
                    <div class="w-32 flex-shrink-0">
                        {{ '會員姓名' }}
                    </div>
                    <div>{{ eventTicket.custFullName }}</div>
                </div>
                <div class="w-full md:w-5/12 mb-2 flex">
                    <div class="w-32 flex-shrink-0">
                        {{ '門票狀況' }}
                    </div>
                    <div class="text-3xl" :class="ticketStatus.msgClass">
                        {{ ticketStatus.msg }}
                    </div>
                </div>
                <div v-if="!!isTicketUsed" class="w-full md:w-5/12 mb-2 flex">
                    <div class="w-32 flex-shrink-0">
                        {{ '使用時間' }}
                    </div>
                    <div>
                        {{
                            eventTicket.evntTckUseTs
                                | formatDate('fullDateTime')
                        }}
                    </div>
                </div>
                <div
                    v-if="isTicketActive && isOneTimeTicket && !isTicketExpired"
                    class="w-full flex flex-col items-center"
                >
                    <div>{{ '請輸入驗証碼，以確認使用門票' }}</div>
                    <input
                        class="verify-code-input mt-2"
                        v-model.trim="verifyCode"
                    />
                    <loadingButton
                        class="bg-primary text-white border-0 text-2xl px-8 py-3 mt-2"
                        :status="saveStatus"
                        @click="confirmUseTicket"
                    >
                        {{ '確認使用門票' }}
                    </loadingButton>
                    <errorBox :message="errors.server" />
                </div>
                <div class="w-full flex flex-col items-center mt-2">
                    <bubble
                        v-if="saveStatus === 'success'"
                        borderClass="border-darkGrayishBlue"
                        arrowStyle="point-down"
                    >
                        <template #image>
                            <img :src="bubbleImg" class=" w-16" />
                        </template>
                        <div class="text-lg ">
                            {{ '成功使用門票' }}
                        </div>
                    </bubble>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import spinner from '../../components/spinner.vue';
import loadingButton from '../../components/loadingButton.vue';
import formatDate from '../../components/formatDateFilter';
import errorBox from '../../components/errorBox.vue';
import bubble from '../../components/bubble.vue';
import { instance } from '../../utils/ajax';
import startOfDay from 'date-fns/startOfDay';
import parseISO from 'date-fns/parseISO';
import img1s112 from '../../assets/img/1-112.png';

async function getEventTicketByToken(token) {
    try {
        const result = await instance.get('/api/eventTicket/getDetail', {
            params: { token }
        });
        return result.data;
    } catch (e) {
        /*eslint-disable no-console*/
        console.error('error get event ticket by token');
    }
    return undefined;
}

async function markEventTicketUsed(token, verifyCode) {
    try {
        const formData = new FormData();
        if (token) formData.append('token', token);
        if (verifyCode) formData.append('verifyCode', verifyCode);

        const result = await instance.post(
            '/api/eventTicket/markTicketUsed',
            formData,
            {
                headers: { ContentType: undefined }
            }
        );
        return result.data;
    } catch (e) {
        /*eslint-disable no-console*/
        console.error('error markEventTicketUsed');
        return { success: false, all: 'error.connect' };
    }
}

const statusMsg = {
    Active: {
        msg: '門票有效',
        msgClass: ' text-green'
    },
    Used: {
        msg: '門票已使用',
        msgClass: ' text-primary'
    },
    Void: {
        msg: '門票已失效',
        msgClass: ' text-red'
    }
};

export default {
    components: { spinner, loadingButton, errorBox, bubble },
    filters: { formatDate },
    data() {
        return {
            errors: {},
            loading: false,
            saveStatus: undefined,
            eventTicket: undefined,
            verifyCode: undefined,
            bubbleImg: img1s112
        };
    },
    async beforeMount() {
        this.loading = true;
        const result = await getEventTicketByToken(this.$route.params.token);
        this.eventTicket = result?.data?.eventTicket;
        if (!this.eventTicket) {
            this.$router.push({ name: 'pageNotFound' });
            return;
        }
        this.loading = false;
    },
    methods: {
        async confirmUseTicket() {
            this.errors = {};
            this.saveStatus = 'loading';
            const result = await markEventTicketUsed(
                this.eventTicket.evntTckAccsTknNum,
                this.verifyCode
            );
            if (result.success) {
                this.saveStatus = 'success';
                this.eventTicket = {
                    ...this.eventTicket,
                    ...result.data.eventTicket
                };
            } else {
                this.saveStatus = undefined;
                this.errors = result.data.errors;
            }
        }
    },
    computed: {
        isOneTimeTicket() {
            return this.eventTicket?.oneTimeEvntTckInd === 'Y';
        },
        isTicketUsed() {
            return this.eventTicket?.evntTckUseTs;
        },
        isTicketActive() {
            return this.eventTicket?.evntTckStsCode === 'A';
        },
        isTicketExpired() {
            // Data Type: Date
            const evntTckEndDate = this.eventTicket?.evntTckEndDate;
            return (
                evntTckEndDate &&
                startOfDay(new Date()).getTime() >
                    parseISO(evntTckEndDate).getTime()
            );
        },
        ticketStatus() {
            const evntTckStsCode = this.eventTicket?.evntTckStsCode;
            if (evntTckStsCode === 'A') {
                return !this.isTicketExpired && !this.isTicketUsed
                    ? statusMsg.Active
                    : statusMsg.Void;
            }
            if (evntTckStsCode === 'C') {
                return statusMsg.Used;
            }
            if (evntTckStsCode === 'X') {
                return statusMsg.Void;
            }
            return statusMsg.Void;
        }
    }
};
</script>
